import { Check, FolderOpen, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";

export type MyAsset = {
  url: string;
  label: string;
  source: "uploaded" | "generated";
  projectId: string;
  projectTitle: string;
  date: string;
};

type SourceFilter = "all" | "uploaded" | "generated";

interface MyAssetsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the picked assets in selection order. */
  onConfirm: (items: Array<{ url: string; label: string }>) => void;
  /** Selection cap. 1 = radio behavior (a new pick replaces the old). */
  maxSelect?: number;
  /** Urls already in use by the caller — shown as "Added", not selectable. */
  excludeUrls?: string[];
  /** Renders above the stepper edit modal (zIndex 90) and shadcn dialogs
   *  (z-50). Rendered inline (no portal): inside a Radix DialogContent the
   *  overlay must be part of the content's DOM subtree, or every click in it
   *  counts as an "outside interaction" and dismisses the parent dialog. */
  zIndex?: number;
}

/** Cross-project image library: everything the user uploaded or generated
 *  across their Director projects, filterable by project and source. */
export default function MyAssetsDialog({
  open,
  onClose,
  onConfirm,
  maxSelect,
  excludeUrls,
  zIndex = 130,
}: MyAssetsDialogProps) {
  const [selected, setSelected] = useState<MyAsset[]>([]);
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");

  const query = trpc.director.listImageAssets.useQuery(undefined, {
    enabled: open,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (open) {
      setSelected([]);
      setProjectFilter("all");
      setSourceFilter("all");
    }
  }, [open]);

  // Close only this overlay on Escape. Capture + stopImmediatePropagation so
  // a parent Radix dialog (reference picker) doesn't also dismiss itself.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  const excluded = useMemo(() => new Set(excludeUrls ?? []), [excludeUrls]);
  const assets = query.data?.assets ?? [];
  const projects = query.data?.projects ?? [];

  const filtered = useMemo(
    () =>
      assets.filter(
        (a) =>
          (projectFilter === "all" || a.projectId === projectFilter) &&
          (sourceFilter === "all" || a.source === sourceFilter),
      ),
    [assets, projectFilter, sourceFilter],
  );

  // Group by calendar day (project updatedAt) like the reference design.
  const groups = useMemo(() => {
    const byDay = new Map<string, MyAsset[]>();
    for (const asset of filtered) {
      const day = new Date(asset.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const list = byDay.get(day);
      if (list) list.push(asset);
      else byDay.set(day, [asset]);
    }
    return Array.from(byDay.entries());
  }, [filtered]);

  if (!open) return null;

  const toggle = (asset: MyAsset) => {
    if (excluded.has(asset.url)) return;
    setSelected((prev) => {
      if (prev.some((s) => s.url === asset.url)) {
        return prev.filter((s) => s.url !== asset.url);
      }
      if (maxSelect === 1) return [asset];
      if (maxSelect !== undefined && prev.length >= maxSelect) return prev;
      return [...prev, asset];
    });
  };

  const atCap = maxSelect !== undefined && selected.length >= maxSelect;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label="My Assets"
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-4xl max-h-[85vh] flex flex-col rounded-2xl border border-white/10 bg-[oklch(0.14_0.02_270)] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3 shrink-0">
          <h2 className="text-lg font-semibold text-white">My Assets</h2>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={selected.length === 0}
              onClick={() => {
                onConfirm(
                  selected.map(({ url, label }) => ({ url, label })),
                );
              }}
              className="bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40"
            >
              Confirm
              {selected.length > 0 ? ` (${selected.length})` : ""}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 pb-3 shrink-0">
          <label className="flex items-center gap-2 text-xs text-white/50">
            Project:
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="h-8 max-w-[180px] rounded-lg border border-white/10 bg-white/[0.05] px-2 text-xs text-white/90 outline-none focus:border-white/30"
            >
              <option value="all">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-white/50">
            Source:
            <select
              value={sourceFilter}
              onChange={(e) =>
                setSourceFilter(e.target.value as SourceFilter)
              }
              className="h-8 rounded-lg border border-white/10 bg-white/[0.05] px-2 text-xs text-white/90 outline-none focus:border-white/30"
            >
              <option value="all">All</option>
              <option value="uploaded">Uploaded</option>
              <option value="generated">Generated</option>
            </select>
          </label>
          {maxSelect !== undefined && maxSelect > 1 && (
            <span className="text-[11px] text-white/40">
              Up to {maxSelect}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
          {query.isLoading ? (
            <div className="flex items-center justify-center py-16 text-white/50">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : query.isError ? (
            <div className="py-16 text-center text-sm text-white/50">
              Couldn't load your assets. Please try again.
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-white/40">
              <FolderOpen className="h-8 w-8" />
              <p className="text-sm">
                {assets.length === 0
                  ? "No images yet — they'll appear here as you upload photos or generate videos."
                  : "No images match these filters."}
              </p>
            </div>
          ) : (
            groups.map(([day, items]) => (
              <div key={day} className="mb-5">
                <div className="text-[11px] text-white/40 mb-2">{day}</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {items.map((asset) => {
                    const isSelected = selected.some(
                      (s) => s.url === asset.url,
                    );
                    const isExcluded = excluded.has(asset.url);
                    const dimmed =
                      isExcluded || (atCap && !isSelected && maxSelect !== 1);
                    return (
                      <button
                        key={asset.url}
                        type="button"
                        onClick={() => toggle(asset)}
                        disabled={isExcluded}
                        title={`${asset.label} · ${asset.projectTitle}`}
                        aria-pressed={isSelected}
                        className={`group relative aspect-[4/3] rounded-xl overflow-hidden border transition ${
                          isSelected
                            ? "border-violet-400"
                            : "border-white/10 hover:border-white/30"
                        } ${dimmed && !isSelected ? "opacity-50" : ""} ${
                          isExcluded ? "cursor-default" : ""
                        }`}
                      >
                        <img
                          src={asset.url}
                          alt={asset.label}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                        <span
                          className={`absolute top-1.5 left-1.5 h-5 w-5 rounded-full border flex items-center justify-center ${
                            isSelected
                              ? "bg-violet-500 border-violet-300"
                              : "bg-black/40 border-white/60"
                          }`}
                          aria-hidden="true"
                        >
                          {isSelected && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </span>
                        {isExcluded && (
                          <span className="absolute inset-x-0 bottom-0 bg-black/70 py-0.5 text-center text-[10px] text-white/80">
                            Added
                          </span>
                        )}
                        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 pt-3 pb-1 text-left text-[10px] text-white/90 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                          {asset.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
